import { zodResolver } from '@hookform/resolvers/zod';
import {
  type AcceptInviteInput,
  type AuthResponse,
  acceptInviteInput,
  ROLE_META,
  type Role,
} from '@hudhuria/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { FieldError, PrimaryButton, RingLogo, Spinner, TextField } from '~/components/ui';
import { api, ok } from '~/lib/api';
import { completeSignIn } from '~/lib/auth';
import { session } from '~/session/session';

export const Route = createFileRoute('/invite/$token')({ component: AcceptInvite });

interface InvitePreview {
  email: string;
  role: Role;
  institutionName: string;
  centers: string[];
}

/** Invited staff set their name, password and front-desk PIN. */
function AcceptInvite() {
  const navigate = useNavigate();
  const { token } = Route.useParams();
  const preview = useQuery({
    queryKey: ['invite', token],
    queryFn: () => ok<InvitePreview>(api.auth.invites[':token'].$get({ param: { token } })),
    retry: false,
    gcTime: 0,
  });
  const form = useForm<AcceptInviteInput>({
    resolver: zodResolver(acceptInviteInput),
    defaultValues: { name: '', password: '', pin: '' },
  });
  const accept = useMutation({
    mutationFn: (v: AcceptInviteInput) =>
      ok<AuthResponse>(api.auth.invites[':token'].accept.$post({ param: { token }, json: v })),
    onSuccess: (res) => {
      session.setOnboarded();
      void navigate({ to: completeSignIn(res), replace: true });
    },
    onError: (e) => form.setError('root', { message: e.message }),
  });
  const { errors } = form.formState;

  if (preview.isPending) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner size={28} />
      </div>
    );
  }
  if (preview.isError) {
    return (
      <div className="flex flex-1 flex-col px-[26px] pt-safe-10 pb-safe">
        <RingLogo size={54} />
        <div className="mt-[26px] font-display text-[28px] font-bold tracking-[-0.02em]">
          Invite not available
        </div>
        <div className="mt-2 text-[15px] text-slate-500">
          {preview.error.message}. Ask your admin to send a new one.
        </div>
        <div className="mt-auto">
          <PrimaryButton onClick={() => void navigate({ to: '/sign-in' })}>Go to sign in</PrimaryButton>
        </div>
      </div>
    );
  }
  const inv = preview.data;
  const meta = ROLE_META[inv.role];

  return (
    <form
      onSubmit={form.handleSubmit((v) => accept.mutate(v))}
      className="flex flex-1 animate-fade-in flex-col px-[26px] pt-safe-10 pb-safe scroll-y"
      noValidate
    >
      <RingLogo size={54} />
      <div className="mt-[26px] font-display text-[28px] leading-[1.1] font-bold tracking-[-0.02em]">
        Join {inv.institutionName}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-[14px] text-slate-500">
        <span
          className="rounded-[7px] px-2 py-[3px] text-[11.5px] font-bold"
          style={{ background: meta.bg, color: meta.fg }}
        >
          {meta.label}
        </span>
        {inv.centers.join(', ')}
      </div>
      <div className="mt-1 text-[13px] text-slate-400">{inv.email}</div>
      <div className="mt-[26px] flex flex-col gap-3">
        <TextField
          label="Full name"
          autoComplete="name"
          placeholder="e.g. Grace Wafula"
          error={errors.name?.message}
          {...form.register('name')}
        />
        <TextField
          label="Password"
          type="password"
          autoComplete="new-password"
          placeholder="At least 8 characters"
          error={errors.password?.message}
          {...form.register('password')}
        />
        <TextField
          label="4-digit PIN (for signing in at the front desk)"
          type="password"
          inputMode="numeric"
          maxLength={4}
          autoComplete="off"
          placeholder="••••"
          error={errors.pin?.message}
          {...form.register('pin')}
        />
        <FieldError>{errors.root?.message}</FieldError>
      </div>
      <div className="mt-auto pt-6">
        <PrimaryButton type="submit" loading={accept.isPending}>
          Create account
        </PrimaryButton>
      </div>
    </form>
  );
}
